import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLinkClickHandler,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  ACTIVE_CONTENT_GROUP_TAB_URL_SEARCH_KEY_PREFIX,
  ApplicationSwitcher,
  AppShell,
  NavigationHeader,
  PageHeader,
  PageHeaderNavigation,
  UserMenu,
} from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { useLoginMode } from '../api/portalApi/loginMode';
import { useLoginInfoQuery } from '../api/portalGateway/loginInfo';
import { useNavigationQuery } from '../api/portalGateway/navigation';
import { fetchPage, PageQueryKey } from '../api/portalGateway/page';
import { useTenantInfoData } from '../api/portalGateway/tenantInfo';
import { usePortalTheme } from '../app/usePortalTheme';
import { AiAssistantLayout } from '../components/ai-assistant/AiAssistantLayout';
import { DashboardContainer } from '../components/customizableDashboard/DashboardContainer';
import { DataRefreshDateNavigationItem } from '../components/datarefresh/DataRefreshDateNavigationItem';
import { CustomPageFilterModal } from '../components/filter/CustomPageFilterModal';
import { PageFilters } from '../components/filter/PageFilters';
import { Navigation } from '../components/navigation/Navigation';
import { NavigationContextProvider } from '../components/navigation/NavigationContext';
import { TopNavigation } from '../components/navigation/TopNavigation';
import { useNavigationCustomizationQuery } from '../components/navigation/useNavigationCustomizationQuery';
import { getNavigationItemBySlug } from '../components/page/getNavigationItemBySlug';
import { PageContentRenderer } from '../components/page/PageContentRenderer';
import { PageCustomizationButton } from '../components/page/PageCustomizationButton';
import { PageEditActions } from '../components/page/PageEditActions';
import { PageWithErrorBoundary } from '../components/page/PageWithErrorBoundary';
import { PageSidebarLayout } from '../components/page-sidebar/PageSidebarLayout';
import { AI_CHAT_FEATURE_FLAG } from '../components/page-sidebar/types';
import { ZipUploadButton } from '../components/zipUpload/ZipUploadButton';
import { POCO_APP_URL, USER_MANAGEMENT_APP_URL } from '../config';
import { useActiveCustomPageFilters } from '../hooks/useActiveCustomPageFilters';
import { useCreateDynamicNavItem } from '../hooks/useCreateDynamicNavItem';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useGenerateDetailPageNavItemLabels } from '../hooks/useGenerateDetailPageNavItemLabels';
import { useGetPageCacheKey } from '../hooks/useGetPageCacheKey';
import { usePageFilterPersistence } from '../hooks/usePageFilterPersistence';
import { usePresetGlobalFiltersFromUrl } from '../hooks/usePresetGlobalFiltersFromUrl';
import { usePresetPageFilters } from '../hooks/usePresetPageFilters';
import { usePresetTableFiltersFromUrl } from '../hooks/usePresetTableFiltersFromUrl';
import {
  STAKE_ADJUSTED_URL_KEY,
  useSyncAllFiltersToSearchParams,
} from '../hooks/useSyncAllFiltersToSearchParams';
import { useStore } from '../store';
import {
  useIsPageEditMode,
  usePageEditModeStore,
} from '../store/states/pageEditMode';
import { useStakeAdjustmentStore } from '../store/states/stakeAdjustment';
import { getActiveTabNavigationKeySuffix } from '../utils/activeTabNavigationKey';
import { isNotNull, isNull } from '../utils/nullish';
import { regroupWidgetsToContents } from '../utils/pageEditModeHelpers';
import { SLUG_TRAILING_SLASH_PATTERN } from '../utils/pattern';
import { pixelSizeHelperPortalUi } from '../utils/pixelSizeHelperPortalUi';
import { prependRoleId } from '../utils/prependRoleId';
import { queryClient } from '../utils/queryClient';
import {
  hasAnyStakeAdjustmentContent,
  hasVisibleStakeAdjustmentContent,
} from '../utils/stakeAdjustmentHelpers';

import pageHeaderActionsStyles from '../components/page/pageHeaderActions.module.scss';

const EMPTY_USER_SCOPES: string[] = [];

type Props = { pageType: 'page' | 'detailPage' };

export const Main = ({ pageType }: Props) => {
  const { t } = useTranslation();
  const {
    data: navigationResponse,
    error: navigationError,
    isFetched: navigationDataFetched,
  } = useNavigationQuery();

  const { data: tenantInfoData } = useTenantInfoData();

  const [
    isNavigationCollapsed,
    logoutSession,
    setNavigationCollapsed,
    userEmail,
    userScopes,
    setGlobalFilterEntityId,
    currentUserId,
  ] = useStore(
    useShallow((state) => [
      state.isNavigationCollapsed,
      state.logoutSession,
      state.setNavigationCollapsed,
      state.session?.getUserEmail(),
      state.session?.getDecodedAuthToken()?.identity?.scopes ??
        EMPTY_USER_SCOPES,
      state.setGlobalFilterEntityId,
      state.session?.getUserId(),
    ])
  );

  const { data: loginModeData } = useLoginMode(userEmail);

  const navigate = useNavigate();
  const { roleId, '*': slug } = useParams();
  const [urlSearchParams] = useSearchParams();

  const searchSlug = slug?.replace(SLUG_TRAILING_SLASH_PATTERN, '$1');

  const activeNavigationItem = searchSlug
    ? getNavigationItemBySlug(
        navigationResponse?.[
          pageType === 'detailPage'
            ? 'detailPageRootNavigationItems'
            : 'rootNavigationItems'
        ] ?? [],
        searchSlug
      )
    : undefined;

  const pageId =
    activeNavigationItem?.type === 'navigation-page-item'
      ? activeNavigationItem.pageId
      : null;

  const { data: resPage, error: resPageError } = useQuery({
    queryKey: prependRoleId<PageQueryKey>(['page', pageId ?? 'unknown']),
    queryFn: fetchPage,
    enabled: Boolean(pageId),
  });

  const pageDashboardNavItems = pageId
    ? navigationResponse?.dashboardNavItems?.[pageId]
    : undefined;
  const customizablePageId = pageDashboardNavItems ? pageId : null;

  const isPageEditMode = useIsPageEditMode(resPage?.data.id ?? null);
  const enterPageEditMode = usePageEditModeStore(
    (state) => state.enterPageEditMode
  );
  const exitPageEditMode = usePageEditModeStore(
    (state) => state.exitPageEditMode
  );
  const contentDraft = usePageEditModeStore((state) => state.contentDraft);

  useEffect(() => exitPageEditMode, [resPage?.data.id, exitPageEditMode]);

  const pageContentsForRender = useMemo(() => {
    if (
      isNotNull(resPage?.data.id) &&
      isPageEditMode &&
      isNotNull(contentDraft)
    ) {
      return regroupWidgetsToContents(contentDraft);
    }
    return resPage?.data.contents;
  }, [contentDraft, isPageEditMode, resPage?.data.contents, resPage?.data.id]);

  const pageFilterConfig = resPage?.data.pageFilter;
  // set filter key for page filters
  // pages use <pageId>, detail pages use <pageId>/<detailsDiscriminator>/<detailsValue> or <pageId>/<detailPageIdentity>
  const instanceId = useGetPageCacheKey(pageType, pageId, resPage?.data);

  const {
    persistenceMode: derivedPersistenceMode,
    persistedSelections,
    persistedCustomSelections,
    isLoading: isPageFilterPersistenceLoading,
    onPersistenceClick,
  } = usePageFilterPersistence({
    persistenceReference: instanceId,
    filterStoreInstanceId: instanceId,
    persistenceConfig: isNotNull(resPage?.data.pageFilterPersistence)
      ? {
          pageFilterPersistence: resPage.data.pageFilterPersistence,
          pageFilterPersistenceScope: resPage.data.pageFilterPersistenceScope,
        }
      : undefined,
    pageFilterConfig,
  });

  // create a dynamic navigation item
  const navItemId = useCreateDynamicNavItem(
    activeNavigationItem,
    resPage?.data
  );

  useGenerateDetailPageNavItemLabels({
    pageId,
    pageData: resPage?.data,
    instanceId,
    navItemId,
    pageType,
  });

  const activeTabValues = useMemo(() => {
    const prefix = ACTIVE_CONTENT_GROUP_TAB_URL_SEARCH_KEY_PREFIX + '-';
    const values: Record<string, string> = {};
    for (const [key, value] of urlSearchParams.entries()) {
      if (key.startsWith(prefix)) {
        values[key.slice(prefix.length)] = value;
      }
    }
    return values;
  }, [urlSearchParams]);

  // preset filters from url search params
  usePresetPageFilters({
    pageData: resPage?.data,
    instanceId,
    persistedSelections,
    persistedCustomSelections,
    persistedSelectionsLoaded:
      isNull(resPage?.data.pageFilterPersistence) ||
      !isPageFilterPersistenceLoading,
    activeTabValues,
  });
  usePresetTableFiltersFromUrl(resPage?.data, instanceId);
  usePresetGlobalFiltersFromUrl({
    config: navigationResponse?.globalFilters,
    instanceId: navigationResponse?.rootPageId ?? null,
  });

  useEffect(() => {
    setGlobalFilterEntityId(navigationResponse?.rootPageId ?? null);
  }, [navigationResponse?.rootPageId, setGlobalFilterEntityId]);

  // determine if page has any stake adjusted content (also checked across grouped-tabs)
  // if true sync page-level-stake-adjustment state with URL.
  const pageHasAnyStakeAdjustment =
    isNotNull(resPage?.data) &&
    hasAnyStakeAdjustmentContent(resPage.data.contents);

  // sync all filter levels to url in a single update
  useSyncAllFiltersToSearchParams({
    globalFilterConfig: navigationResponse?.globalFilters,
    globalFilterInstanceId: navigationResponse?.rootPageId ?? null,
    pageFilterConfig,
    pageFilterInstanceId: instanceId,
    navigationData: activeNavigationItem,
    navItemId,
    pageData: resPage?.data,
    pageHasAnyStakeAdjustment,
    enableStakeAdjustmentByDefault:
      resPage?.data.enableStakeAdjustmentByDefault,
    activeTabValues,
  });

  const logout = useCallback(async () => {
    await logoutSession({ initiatedByUser: true });
  }, [logoutSession]);

  const errorMessage = (navigationError ?? resPageError)?.message ?? null;

  const pageContent = errorMessage
    ? PageContentRenderer({
        pageNode: {
          type: 'error',
          message: errorMessage,
        },
        Render: PageContentRenderer,
        pageId: 'unknown',
        instanceId: 'unknown',
      })
    : pageContentsForRender?.map((content, index) => {
        const resolvedPageId = pageId ?? 'unknown';
        const childKey =
          content.type === 'widget'
            ? `${resolvedPageId}-${content.id}`
            : `${resolvedPageId}-${index}`;
        return PageContentRenderer({
          pageNode: content,
          Render: PageContentRenderer,
          pageId: resolvedPageId,
          instanceId: instanceId ?? 'unknown',
          child_key: childKey,
        });
      });

  useDocumentTitle(
    pageType === 'detailPage'
      ? resPage?.data.pageHeader?.title
      : activeNavigationItem?.completeSlug
  );

  useEffect(() => {
    document.body.classList.add('portal-scene--main');
    return () => document.body.classList.remove('portal-scene--main');
  }, []);

  // Keep refs so the navigation effect can read the latest values without
  // re-running every time the URL changes.  React Router v6's navigate and
  // urlSearchParams are unstable (new reference on every URL / pathname
  // change), so they must NOT be effect dependencies.
  const urlSearchParamsRef = useRef(urlSearchParams);
  urlSearchParamsRef.current = urlSearchParams;

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (
      navigationDataFetched &&
      navigationResponse &&
      isNull(activeNavigationItem)
    ) {
      navigateRef.current(navigationResponse.initialPageSlug, {
        replace: true,
      });
    }
    if (activeNavigationItem?.type === 'navigation-menu-item') {
      const currentParams = urlSearchParamsRef.current;
      navigateRef.current(
        `${activeNavigationItem.pageSlug}${
          currentParams.toString().length > 0
            ? '?' + currentParams.toString()
            : ''
        }`,
        {
          replace: true,
        }
      );
    }
  }, [navigationResponse, activeNavigationItem, navigationDataFetched]);

  const { data: dynamicNavigationItems } = useNavigationCustomizationQuery();

  const currentDynamicNavigationItem =
    dynamicNavigationItems?.data.dynamicNavigationItems.find(
      (item) => item.id === navItemId
    );
  const navigateBackClickHandler = useLinkClickHandler<HTMLDivElement>(
    currentDynamicNavigationItem?.originHref ?? ''
  );

  const showPasswordChange =
    !loginModeData ||
    loginModeData.mode !== 'sso' ||
    loginModeData.changePasswordUrl;

  const hasPageFilters = Boolean(pageFilterConfig?.filters.length);
  const { hasActiveCustomPageFilters, hasCustomizablePageFilters } =
    useActiveCustomPageFilters(pageFilterConfig, instanceId);

  const hasStakeAdjustment =
    isNotNull(resPage?.data) &&
    hasVisibleStakeAdjustmentContent(resPage.data.contents, activeTabValues);

  // Initialise the stake-adjustment store from URL params or page defaults.
  useEffect(() => {
    if (pageHasAnyStakeAdjustment && isNotNull(instanceId)) {
      const {
        stakeAdjusted: { [instanceId]: currentValue },
        setStakeAdjusted,
      } = useStakeAdjustmentStore.getState();

      if (currentValue === undefined) {
        const urlValue = urlSearchParamsRef.current.get(STAKE_ADJUSTED_URL_KEY);
        const initialValue = isNotNull(urlValue)
          ? urlValue === 'true'
          : (resPage?.data.enableStakeAdjustmentByDefault ?? false);
        setStakeAdjusted(instanceId, initialValue);
      }
    }
  }, [
    pageHasAnyStakeAdjustment,
    instanceId,
    resPage?.data.enableStakeAdjustmentByDefault,
  ]);

  const goToPasswordChange = () => {
    if (loginModeData?.changePasswordUrl) {
      globalThis.location.assign(loginModeData?.changePasswordUrl);
      return;
    } else {
      navigate('/changePassword');
    }
  };

  const currentMainSlug = searchSlug?.split('/').shift();
  const currentNavigationTree = navigationResponse?.[
    pageType === 'page'
      ? 'rootNavigationItems'
      : 'detailPageRootNavigationItems'
  ]?.find((nav) => nav.slug === currentMainSlug);
  const hasSubnavigation =
    currentNavigationTree &&
    'items' in currentNavigationTree &&
    currentNavigationTree.items.length > 0;

  const { userCanSwitchTheme } = usePortalTheme();
  const { data: loginInfo } = useLoginInfoQuery();

  const navigationKey = useMemo(() => {
    if (pageType === 'detailPage' && isNull(instanceId)) {
      return null;
    }

    const pageNavigationKey = instanceId ?? pageId ?? searchSlug ?? null;
    const tabNavigationKey = getActiveTabNavigationKeySuffix(activeTabValues);
    const navigationParts = [pageNavigationKey, tabNavigationKey].filter(
      (part) => isNotNull(part) && part.length > 0
    );
    const pageKey =
      navigationParts.length > 0 ? navigationParts.join('/') : null;

    return isNotNull(roleId) && isNotNull(pageKey)
      ? `${roleId}/${pageKey}`
      : pageKey;
  }, [activeTabValues, instanceId, pageId, pageType, roleId, searchSlug]);

  const applicationSwitcher = (
    <ApplicationSwitcher
      scopes={userScopes}
      urls={{
        'portal-configurator': POCO_APP_URL,
        'user-management': USER_MANAGEMENT_APP_URL,
      }}
    />
  );

  const userMenuButton = (
    <UserMenu
      onLanguageSaved={() => {
        void queryClient.invalidateQueries();
      }}
      tenantDefaults={
        loginInfo
          ? {
              language: loginInfo.localeMessages,
              languages: loginInfo.languages,
              colorScheme: loginInfo.theme?.activeTheme,
            }
          : undefined
      }
      onChangePassword={showPasswordChange ? goToPasswordChange : undefined}
      onLogout={logout}
      userCanSwitchTheme={userCanSwitchTheme}
      userEmail={userEmail}
    />
  );

  const pageHeaderNavigation = currentDynamicNavigationItem?.originHref && (
    <PageHeaderNavigation
      label={t('app.backToPage')}
      onClick={navigateBackClickHandler}
    />
  );

  const pageActions = isPageEditMode ? (
    <PageEditActions />
  ) : (
    <PageCustomizationButton
      enabled={resPage?.data.enablePageCustomization}
      onClick={() => {
        if (isNull(resPage?.data)) {
          return;
        }
        enterPageEditMode(resPage.data.id, resPage.data.contents);
      }}
    />
  );

  const isPageFilterEnabled =
    pageId &&
    instanceId &&
    (hasStakeAdjustment || hasPageFilters || hasCustomizablePageFilters);
  const pageFilter = isPageFilterEnabled && (
    <PageFilters
      config={pageFilterConfig}
      filterKey={instanceId}
      persistenceMode={derivedPersistenceMode}
      onPersistenceClick={onPersistenceClick}
      pageId={pageId}
      activeDynamicNavigationItem={currentDynamicNavigationItem}
      hasStakeAdjustment={hasStakeAdjustment}
      stakeAdjustmentDefaultEnabled={
        resPage?.data.enableStakeAdjustmentByDefault
      }
    />
  );
  const hasVisiblePageFilters = Boolean(
    pageFilterConfig?.filters.filter((f) => !f.hidden).length ||
    hasStakeAdjustment ||
    hasActiveCustomPageFilters
  );

  const zipUploadButton = tenantInfoData?.enableGlobalZipUpload && (
    <ZipUploadButton />
  );

  return (
    <AppShell
      navigationHeader={
        <NavigationHeader
          applicationSwitcher={applicationSwitcher}
          collapsed={isNavigationCollapsed}
          onCollapseClick={() => setNavigationCollapsed(!isNavigationCollapsed)}
          userIcon={userMenuButton}
          zipUploadButton={zipUploadButton}
        />
      }
      navigation={
        <NavigationContextProvider>
          <Navigation
            applicationSwitcher={applicationSwitcher}
            globalFilters={navigationResponse?.globalFilters}
            rootPageId={navigationResponse?.rootPageId}
            userMenuButton={userMenuButton}
          />
        </NavigationContextProvider>
      }
      navigationFooter={
        activeNavigationItem?.type === 'navigation-page-item' && (
          <DataRefreshDateNavigationItem
            collapsed={isNavigationCollapsed}
            navigationData={activeNavigationItem}
          />
        )
      }
      navigationCollapsed={isNavigationCollapsed}
      navigationWidthCollapsed={pixelSizeHelperPortalUi(64)}
      navigationWidthExpanded={pixelSizeHelperPortalUi(300)}
    >
      <AiAssistantLayout
        enabled={
          tenantInfoData?.enabledFeatures?.includes(AI_CHAT_FEATURE_FLAG) ??
          false
        }
      >
        {isNotNull(customizablePageId) ? (
          <DashboardContainer
            sourcePageId={customizablePageId}
            pageDashboardNavItems={pageDashboardNavItems}
            navigationKey={navigationKey}
            currentUserId={currentUserId}
            page={resPage?.data}
            navigationError={navigationError}
            pageError={resPageError}
            fallbackTitle={activeNavigationItem?.label}
            pageHeaderNavigation={pageHeaderNavigation}
            slug={slug}
            currentDynamicNavigationItem={currentDynamicNavigationItem}
            classicPageActions={pageActions}
          />
        ) : (
          <PageSidebarLayout
            navigationKey={navigationKey}
            topNavigation={
              hasSubnavigation ? (
                <TopNavigation
                  pageType={pageType}
                  navigationData={navigationResponse}
                  slug={slug}
                  activeDynamicNavigationItem={currentDynamicNavigationItem}
                />
              ) : undefined
            }
          >
            <PageWithErrorBoundary
              pageHeaderNavigation={pageHeaderNavigation}
              pageHeader={
                activeNavigationItem?.label && (
                  <PageHeader
                    title={
                      resPage?.data.pageHeader?.title ||
                      activeNavigationItem.label
                    }
                  >
                    {isNotNull(resPage?.data) && (
                      <div className={pageHeaderActionsStyles['actions']}>
                        <CustomPageFilterModal
                          page={resPage.data}
                          filterInstanceId={instanceId}
                        />
                        {resPage.data.enablePageCustomization !== true &&
                          pageActions}
                      </div>
                    )}
                  </PageHeader>
                )
              }
              pageFilter={pageFilter}
              pageContent={pageContent}
              slug={currentDynamicNavigationItem?.href ?? slug}
              activeDynamicNavigationItem={currentDynamicNavigationItem}
              hasVisiblePageFilters={hasVisiblePageFilters}
            />
          </PageSidebarLayout>
        )}
      </AiAssistantLayout>
    </AppShell>
  );
};
